import React from 'react';
import NewsView from './NewsView';
import TaskNews from '../../ws/TaskNews';
import { CenterManager, LoadingView, Role } from '@cuatroochenta/co-admin-react-library';
import { Post } from '../../model/domain/news';
import { User } from '../../model/domain/user';

interface ApiResponse {
    message: string;
    data: {
        total_pages: number;
        total_count: number;
        current_page: number;
        limit: number;
        news: Post[];
    };
}

interface State {
    posts: Post[];
    loading: boolean;
    error: Error | null;
    currentPage: number;
    totalPages: number;
    fetchingMore: boolean;
    media: { [key: number]: {[key:number]: string | null} | null };
    filters: {
        dateFrom: string;
        dateTo: string;
        types: string;
    };
    user: User;
}

class NewsContainer extends React.Component<{}, State> {
    state: State = {
        posts: [],
        loading: true,
        error: null,
        currentPage: 1,
        totalPages: 1,
        fetchingMore: false,
        media: {},
        filters: {
            dateFrom: '',
            dateTo: '',
            types: ''
        },
        user: {
            success: false,
            code: 0,
            data: {
                id: 0,
                fullName: '',
                email: '',
                address: null,
                city: null,
                county: null,
                phone: null,
                postalCode: null,
                userName: '',
                profileColor: undefined,
                roles: [],
                ddbbResiplusId: -1,
                createNewsPermission: false,
                profilePicturePath: null
            }
        },
    };

    componentDidMount() {
        this.fetchUser();
        window.addEventListener('scroll', this.handleScroll, true);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll, true);
    }

    private generateRandomColor() {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }

    fetchUser = () => {
        new TaskNews().getUser()
            .then((user: User) => {
                if (!user.data.profileColor) {
                    // Generar color aleatorio
                    const randomColor = this.generateRandomColor();

                    // Llamar al método updateUserColor con el color generado
                    new TaskNews().updateUserColor(randomColor, user.data.fullName, user.data.email, user.data.phone)
                        .then(() => {
                            console.log("Color de usuario actualizado");

                            // Después de actualizar el color, se actualiza el estado
                            user.data.profileColor = randomColor;
                            this.setState({ user });
                            this.fetchNews();
                        })
                        .catch(error => {
                            console.error("Error al actualizar el color del usuario:", error);
                        });
                } else {
                    // Si el color ya está definido, solo actualizar el estado
                    this.setState({ user });
                    this.fetchNews();
                }

            })
            .catch(error => {
                console.error('Error al obtener el usuario:', error);
                this.setState({ error });
            });
    };

    // Método para cargar las noticias de la API
    fetchNews = (page: number = 1, filters: any = this.state.filters, isUpdate: boolean = false) => {
        this.setState({ loading: !isUpdate });

        const url = this.buildUrl(filters, page);

        new TaskNews().fetchNews(url)
            .then((data: ApiResponse) => {
                if (data && data.data) {
                    const newPosts = data.data.news;

                    this.setState(prevState => {
                        let updatedPosts;
    
                        if (isUpdate) {
                            updatedPosts = prevState.posts.map(existingPost => {
                                const updatedPost = newPosts.find(newPost => newPost.id === existingPost.id);
                                if (updatedPost) {
                                    this.updateMediaBlobsForPost(updatedPost);
                                }
                                return updatedPost || existingPost;
                            });
                        } else {
                            updatedPosts = [...prevState.posts, ...newPosts];
                            this.loadMediaUrls(newPosts);
                        }
    
                        return {
                            posts: updatedPosts,
                            totalPages: data.data.total_pages,
                            currentPage: data.data.current_page,
                            loading: false,
                            fetchingMore: false,
                        };
                    });
                } else {
                    this.setState({ error: new Error('No se encontraron noticias'), loading: false });
                }
            })
            .catch((error: Error) => {
                console.error('Error al obtener las noticias:', error);
                this.setState({ error, loading: false, fetchingMore: false });
            });
    };

    private updatePostLikes = (postId: number, totalLikes: any, userLike: {likeTypeId: number, likeTypeName: string}) => {
        this.setState(prevState => ({
            posts: prevState.posts.map(post =>
                post.id === postId
                    ? { ...post, total_likes: totalLikes, user_like: userLike }
                    : post
            )
        }));
    };

    private updateViewData = () => {
        const scrollPosition = window.scrollY;
        for (let page = 1; page <= this.state.currentPage; page++) {
            this.fetchNews(page, this.state.filters, true);
        }
    
        window.scrollTo(0, scrollPosition);
    }

    buildUrl(filters: any, page: number) {
        let url = `?page=${page}`;
        if (filters.dateFrom) url += `&dateFrom=${filters.dateFrom}`;
        if (filters.dateTo) url += `&dateTo=${filters.dateTo}`;
        if (filters.types) url += `&types=${filters.types}`;
        const selectedCenter = CenterManager.getSelectedCenter();
        if (selectedCenter) {
            url += `&centerId=${selectedCenter}`;
        }
        const { user } = this.state;
        url += `&role_id=${user.data.roles.includes(Role.SUPERADMIN)
            ? Role.SUPERADMIN 
            : Role.ADMIN}`;
        return url;
    }

    // Método para cargar las URLs de los medios
    loadMediaUrls(posts: Post[]) {
        const mediaMap: { [key: number]: {[key: number]: string | null} | null } = {};

        posts.forEach(post => {
            post.media?.data.forEach(mediaItem => {
                const url = new TaskNews().getMediaUrlById(mediaItem.id.toString(), post.media!.media_type)
                if (!mediaMap[post.id]) {
                    mediaMap[post.id] = {};
                }
                mediaMap[post.id]![mediaItem.id] = url;
            });
        });

        this.setState(prevState => ({
            media: { ...prevState.media, ...mediaMap }
        }));
    }

    updateMediaBlobsForPost(post: Post) {
        const mediaMap: { [key: number]: { [key: number]: string | null } | null } = {};

        post.media?.data.forEach(mediaItem => {
            const url = new TaskNews().getMediaUrlById(mediaItem.id.toString(), post.media!.media_type)
            if (!mediaMap[post.id]) {
                mediaMap[post.id] = {};
            }
            mediaMap[post.id]![mediaItem.id] = url;
        });

        this.setState(prevState => ({
             media: {
                ...prevState.media,
                [post.id]: {
                    ...prevState.media[post.id],
                    ...mediaMap[post.id]
                }
            }
        }));
    }

    // Detectar cuando llegamos al final del scroll
    handleScroll = () => {
        const { currentPage, totalPages, fetchingMore, loading, filters } = this.state;

        if (loading || fetchingMore) return;
        const isBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 5;

        // Comprobamos si el usuario está cerca del final de la página
        if (isBottom && currentPage < totalPages) {
            this.setState({ fetchingMore: true });
            this.fetchNews(currentPage + 1, filters);
        }
    };

    handleFilterSubmit = (filters: { dateFrom: string; dateTo: string; types: string }) => {
        // Actualizamos el estado con los filtros seleccionados
        this.setState(
            { filters, posts: [], currentPage: 1 }, // Resetear los posts y volver a la primera página
            () => {
                // Llamamos a fetchNews con los filtros actualizados
                this.fetchNews(1, filters);
            }
        );
    };

    render() {
        const { posts, loading, error, fetchingMore, user } = this.state;

        // Mostramos el LoadingView si está cargando
        if (loading && posts.length === 0) {
            return <LoadingView loading={loading} />;
        }

        if (error) {
            return <div>Error: {error.message}</div>;
        }

        return (
            <>
                <NewsView posts={posts} media={this.state.media} onFilterSubmit={this.handleFilterSubmit} user={user} updatePosts={this.updatePostLikes} updateView={this.updateViewData} />
                {fetchingMore && (
                    <div style={{ position: 'relative', marginTop:'5%', width: window.innerWidth < 1024 ? '100%' : '70%', textAlign: 'center', padding: '10px 0', zIndex: 9999 }}>
                        <LoadingView loading={fetchingMore} />
                    </div>
                )}
            </>
        );


    }
}

export default NewsContainer;
